/**
 * Envoi du lien d'UN candidat, depuis la fiche de son parcours : hook d'état
 * + modale de confirmation, partagés par le portail client et le portail admin.
 * Seule l'action appelée diffère : org-scopée côté client, cross-org côté
 * super-admin.
 */
import { Button, Dialog } from "@repo/ui";
import { useAction } from "convex/react";
import { CheckCircle, Loader2, Mail, XCircle } from "lucide-react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";

type StatutEnvoi = "idle" | "sending" | "success" | "error";

export type EnvoiLienParcours = {
  ouvert: boolean;
  statut: StatutEnvoi;
  message: string;
  ouvrir: () => void;
  fermer: () => void;
  envoyer: () => Promise<void>;
};

export function useEnvoiLienParcours({
  parcoursId,
  emailCandidat,
  superAdmin = false,
}: {
  parcoursId: Id<"parcoursEvaluations"> | undefined;
  emailCandidat?: string;
  superAdmin?: boolean;
}): EnvoiLienParcours {
  const [ouvert, setOuvert] = useState(false);
  const [statut, setStatut] = useState<StatutEnvoi>("idle");
  const [message, setMessage] = useState("");

  const envoyerClient = useAction(api.email.envoyerLienParcours);
  const envoyerSuperAdmin = useAction(api.email.envoyerLienParcoursSuperAdmin);
  const envoyerLien = superAdmin ? envoyerSuperAdmin : envoyerClient;

  const reinitialiser = () => {
    setStatut("idle");
    setMessage("");
  };

  const envoyer = async () => {
    if (!parcoursId) return;
    setStatut("sending");
    setMessage("Envoi en cours...");
    try {
      const res = await envoyerLien({ parcoursId });
      if (res.succes) {
        setStatut("success");
        setMessage(`L'email a été envoyé avec succès à ${emailCandidat}`);
      } else {
        setStatut("error");
        setMessage(
          res.message || "Une erreur est survenue lors de l'envoi de l'email.",
        );
      }
    } catch (error) {
      setStatut("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Une erreur inattendue est survenue.",
      );
    }
  };

  return {
    ouvert,
    statut,
    message,
    ouvrir: () => {
      reinitialiser();
      setOuvert(true);
    },
    fermer: () => {
      setOuvert(false);
      reinitialiser();
    },
    envoyer,
  };
}

export function EnvoyerLienParcoursDialog({
  envoi,
  emailCandidat,
  lienEnvoye,
}: {
  envoi: EnvoiLienParcours;
  emailCandidat?: string;
  lienEnvoye?: boolean;
}) {
  const { ouvert, statut, message, fermer, envoyer } = envoi;
  return (
    <Dialog
      isOpen={ouvert}
      onClose={fermer}
      title="Envoyer le lien du parcours"
      className="max-w-md"
    >
      <div className="space-y-4">
        {statut === "idle" && (
          <div className="space-y-2">
            {lienEnvoye && (
              <div className="flex items-center gap-2 text-sm text-etat-fait-contenu bg-etat-fait-surface border border-etat-fait-trait rounded-surface p-2">
                <Mail className="h-4 w-4 text-etat-fait shrink-0" />
                Un lien a déjà été envoyé à ce candidat.
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Un email sera envoyé à <strong>{emailCandidat}</strong> avec le
              lien d'accès au parcours.
            </p>
          </div>
        )}
        {statut === "sending" && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">{message}</span>
          </div>
        )}
        {statut === "success" && (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="h-12 w-12 text-etat-fait" />
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {message}
            </p>
          </div>
        )}
        {statut === "error" && (
          <div className="flex flex-col items-center justify-center py-8">
            <XCircle className="h-12 w-12 text-etat-erreur" />
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {message}
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4">
          {statut === "idle" && (
            <>
              <Button variant="outline" onClick={fermer}>
                Annuler
              </Button>
              <Button onClick={() => void envoyer()}>
                <Mail className="h-4 w-4 mr-2" />
                Envoyer
              </Button>
            </>
          )}
          {(statut === "success" || statut === "error") && (
            <Button onClick={fermer}>Fermer</Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
